"use client";

import { Input } from "@/src/components/shadcn/ui/input";
import { Label } from "@/src/components/shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/shadcn/ui/select";
import { Textarea } from "@/src/components/shadcn/ui/textarea";
import { formatPhoneNumberAsTyped } from "@/src/lib/utils/date-and-phone-utils";

export interface FormFieldOption {
  value: string;
  label: string;
}

export interface FormFieldProps {
  label: string;
  value: string | undefined | null;
  onChange: (value: string) => void;
  type?: "text" | "email" | "date" | "tel" | "select" | "textarea" | "number";
  placeholder?: string;
  required?: boolean;
  options?: FormFieldOption[];
  className?: string;
  error?: string;
  disabled?: boolean;
  rows?: number;
}

export default function FormField({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  required = false,
  options = [],
  className = "",
  error,
  disabled = false,
  rows,
}: FormFieldProps) {
  const handleChange = (newValue: string) => {
    // Auto-format phone numbers as user types
    if (type === "tel") {
      const formattedValue = formatPhoneNumberAsTyped(newValue);

      onChange(formattedValue);
    } else {
      onChange(newValue);
    }
  };

  const renderField = () => {
    const commonProps = {
      value: value || "",
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        handleChange(e.target.value);
      },
      className: `mt-1 bg-input ${error ? "border-red-500" : ""} ${className}`,
      placeholder,
      disabled,
    };

    switch (type) {
      case "select":
        return (
          <Select {...commonProps} value={value || ""} onValueChange={handleChange}>
            <SelectTrigger className={`mt-1 bg-input ${error ? "border-red-500" : ""} ${className}`}>
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case "textarea":
        return (
          <Textarea
            {...commonProps}
            rows={rows}
            className={`bg-input ${commonProps.className} border-zinc-200 focus-visible:ring-zinc-950 placeholder:text-zinc-500 dark:border-zinc-800 dark:focus-visible:ring-zinc-300 dark:placeholder:text-zinc-400 transition-colors`}
          />
        );

      default:
        return <Input {...commonProps} type={type} />;
    }
  };

  const htmlId = label.toLowerCase().replace(/\s+/g, "-");

  return (
    <div>
      <Label htmlFor={htmlId} className="text-sm font-medium">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      {renderField()}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
